import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  BookOpen, MessageSquare, Target, TrendingUp, ArrowRight, CheckCircle, Lock, ChevronRight, BarChart3, Sparkles, Lightbulb
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [latestSummary, setLatestSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [dashRes, summRes] = await Promise.all([
          axios.get(`${API}/dashboard`, { withCredentials: true }),
          axios.get(`${API}/progress/summaries`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setDashboard(dashRes.data);
        if (summRes.data && summRes.data.length > 0) {
          setLatestSummary(summRes.data[summRes.data.length - 1]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const d = dashboard || {};
  const completedSet = new Set(d.completed_modules || []);

  return (
    <div data-testid="dashboard-page" className="p-6 md:p-8 lg:p-12 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-2">Dashboard</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
          Welcome back, {user?.name?.split(' ')[0] || 'Leader'}
        </h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Phase {d.current_phase}: {d.current_phase_name}</p>
      </div>

      {/* Focus Card */}
      {d.current_module && (
        <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 mb-8 hover:border-[#FF3B30]/30 transition-all">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF3B30] mb-2">Your Focus This Week</p>
              <h2 className="text-white font-semibold text-xl mb-2">
                Week {d.current_module.week}: {d.current_module.title}
              </h2>
              <p className="text-[#A3A3A3] text-sm leading-relaxed max-w-2xl">{d.current_module.description}</p>
            </div>
            <Link
              to={`/module/${d.current_module.week}`}
              data-testid="focus-card-link"
              className="shrink-0 bg-[#FF3B30] hover:bg-[#D62828] text-white font-bold px-5 py-2.5 rounded-sm transition-all flex items-center gap-2 text-sm"
            >
              Go to Module <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Reassessment Banner */}
      {d.reassessment_due && (
        <div data-testid="reassessment-banner" className="bg-[#141414] border border-[#F59E0B]/50 rounded-sm p-6 mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#F59E0B]/10 rounded-sm flex items-center justify-center shrink-0">
                <BarChart3 className="w-5 h-5 text-[#F59E0B]" />
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#F59E0B] mb-1">Reassessment Due</p>
                <h3 className="text-white font-semibold text-lg mb-1">
                  {d.reassessment_due === 'week_12' ? 'Week 12 Checkpoint' : 'Week 24 Final Assessment'}
                </h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed max-w-xl">
                  {d.reassessment_due === 'week_12'
                    ? "You've reached the halfway mark. Take the reassessment to measure your growth and recalibrate your coaching focus."
                    : "You've completed the program. Take the final assessment to see how far you've come since day one."
                  }
                </p>
              </div>
            </div>
            <Link
              to={`/assessment?type=${d.reassessment_due}`}
              data-testid="reassessment-link"
              className="shrink-0 bg-[#F59E0B] hover:bg-[#D97706] text-black font-bold px-5 py-2.5 rounded-sm transition-all flex items-center gap-2 text-sm"
            >
              Take Reassessment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { icon: TrendingUp, label: "Progress", value: `${d.completion_percentage || 0}%`, sub: `${(d.completed_modules || []).length}/24 modules` },
          { icon: BookOpen, label: "Current Week", value: `Week ${d.current_week || 1}`, sub: d.current_module?.title || "Getting Started" },
          { icon: MessageSquare, label: "Coach Sessions", value: d.total_coach_sessions || 0, sub: "conversations with AI coach" },
          { icon: Target, label: "Reflections", value: d.total_reflections || 0, sub: "written reflections" },
        ].map(({ icon: Icon, label, value, sub }) => (
          <div key={label} className="bg-[#141414] border border-[#262626] rounded-sm p-5 hover:border-[#FF3B30]/30 transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-[#FF3B30]/10 rounded-sm flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#FF3B30]" />
              </div>
              <span className="text-[#A3A3A3] text-xs tracking-[0.15em] uppercase font-bold">{label}</span>
            </div>
            <p className="text-white font-bold text-2xl">{value}</p>
            <p className="text-[#737373] text-xs mt-1 truncate">{sub}</p>
          </div>
        ))}
      </div>

      {/* Latest Weekly Insight */}
      {latestSummary && (
        <div data-testid="latest-summary-card" className="bg-[#141414] border border-[#FF3B30]/20 rounded-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FF3B30]" />
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#FF3B30]">Latest Insight — Week {latestSummary.week}</span>
            </div>
            <Link to={`/module/${latestSummary.week}`} className="text-[#737373] hover:text-white text-xs transition-colors">
              View full summary &rarr;
            </Link>
          </div>
          {latestSummary.growth_edge && (
            <p className="text-white text-sm leading-relaxed mb-3">{latestSummary.growth_edge}</p>
          )}
          {latestSummary.commitments?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {latestSummary.commitments.map((c, i) => (
                <span key={i} className="text-xs bg-[#22C55E]/10 text-[#22C55E] px-2.5 py-1 rounded-sm">{c}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Progress Grid - GitHub style */}
      <div className="bg-[#141414] border border-[#262626] rounded-sm p-6 mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#A3A3A3] mb-4">24-Week Progress Tracker</p>
        <div className="grid grid-cols-12 gap-2">
          {Array.from({ length: 24 }, (_, i) => {
            const week = i + 1;
            const isComplete = completedSet.has(week);
            const isCurrent = week === (d.current_week || 1);
            return (
              <div
                key={week}
                data-testid={`progress-week-${week}`}
                className={`aspect-square rounded-sm flex items-center justify-center text-xs font-bold transition-all cursor-pointer
                  ${isComplete ? 'bg-[#FF3B30] text-white' : isCurrent ? 'bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/50' : 'bg-[#1A1A1A] text-[#737373] border border-[#262626] hover:border-[#FF3B30]/30'}`}
                title={`Week ${week}${isComplete ? ' (Completed)' : isCurrent ? ' (Current)' : ''}`}
              >
                {isComplete ? <CheckCircle className="w-3.5 h-3.5" /> : week}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-4 mt-4 text-xs text-[#737373]">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#FF3B30] rounded-sm inline-block" /> Completed</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#FF3B30]/20 border border-[#FF3B30]/50 rounded-sm inline-block" /> Current</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#1A1A1A] border border-[#262626] rounded-sm inline-block" /> Upcoming</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/coach"
          data-testid="quick-action-coach"
          className="bg-[#141414] border border-[#262626] rounded-sm p-5 hover:border-[#FF3B30]/50 transition-all group flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-[#FF3B30]/10 rounded-sm flex items-center justify-center group-hover:bg-[#FF3B30]/20 transition-colors">
              <MessageSquare className="w-5 h-5 text-[#FF3B30]" />
            </div>
            <div>
              <p className="text-white font-semibold">Talk to Your AI Coach</p>
              <p className="text-[#737373] text-sm">Get personalized guidance on this week's module</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#737373] group-hover:text-[#FF3B30] transition-colors" />
        </Link>

        <Link
          to="/curriculum"
          data-testid="quick-action-curriculum"
          className="bg-[#141414] border border-[#262626] rounded-sm p-5 hover:border-[#FF3B30]/50 transition-all group flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-[#FF3B30]/10 rounded-sm flex items-center justify-center group-hover:bg-[#FF3B30]/20 transition-colors">
              <BookOpen className="w-5 h-5 text-[#FF3B30]" />
            </div>
            <div>
              <p className="text-white font-semibold">Browse Curriculum</p>
              <p className="text-[#737373] text-sm">Explore all 24 weeks of the program</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#737373] group-hover:text-[#FF3B30] transition-colors" />
        </Link>
      </div>
    </div>
  );
}
